import React, { useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Edit, Trash, Check, AlertCircle, LogOut, Shirt, Printer, Bluetooth, Trash2, Settings, TrendingUp, Truck, User, UserCheck, Users, DollarSign, FileText, KeyRound, ShieldCheck, Building2 } from 'lucide-react';
import Pagination from './Pagination';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const DataTable = ({ data, columns, isLoading, onAction, page, limit, totalRecords, onPageChange, onLimitChange, hideActionsColumn = false, headerClassName = 'bg-primary/50', ...cellRenderProps }) => {
  // ID único por instancia del DataTable. Lo usamos como prefijo de los
  // `layoutId` de Framer Motion para que cuando haya DOS DataTables en
  // la misma página (p. ej. la tabla principal de ciclos y la tabla de
  // ciclos abortados en WashingCyclesPage), Framer no confunda filas
  // con el mismo `_id` o `rowIndex` entre las dos tablas y termine
  // "moviendo" filas de una tabla a otra o dejándolas en blanco.
  // Ver: https://www.framer.com/motion/layout-animations/#layoutid
  const instanceId = useId();
  const layoutIdPrefix = `dt-${instanceId}-`;
  const tableRowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        type: 'spring',
        stiffness: 300,
        damping: 20,
      },
    }),
    exit: { opacity: 0, y: -10, transition: { duration: 0.15 } },
  };

  if (isLoading) {
    return (
      <div className="p-4 bg-card rounded-lg">
        <div className="space-y-3">
          <div className="h-8 bg-muted rounded w-full animate-pulse"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-muted/60 rounded w-full animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center p-6 bg-card rounded-lg shadow-md text-center min-h-[200px]"
      >
        <div className="w-14 h-14 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="h-7 w-7" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1">No hay datos disponibles</h3>
        <p className="text-muted-foreground text-sm max-w-xs">
          Intenta ajustar tus filtros o verifica si hay datos para mostrar.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="overflow-x-auto w-full rounded-lg shadow-sm border border-border custom-scrollbar">
      <table className="w-full table-auto divide-y divide-muted bg-background text-sm">
        <thead className={`${headerClassName} sticky top-0 z-10 backdrop-blur-sm`}>
          <tr>
            {/* Encabezado para columna de opciones/acciones */}
            {!hideActionsColumn && (
              <th key="table-actions-header" className="px-4 py-3 font-semibold text-foreground w-[120px]">
                {columns.find(col => col.key === 'opciones' || col.id === 'opciones') ? 'Opciones' : 'Acciones'}
              </th>
            )}
            {columns.filter(column => 
              column.key !== 'opciones' && 
              column.id !== 'opciones' && 
              column.key !== 'actions' && 
              column.id !== 'actions'
            ).map((column, columnIndex) => (
              <th key={column.id} className="px-4 py-3 text-left font-semibold text-foreground whitespace-nowrap" style={{width: column.width || 'auto'}}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <AnimatePresence mode="popLayout">
            {data.map((row, rowIndex) => {
              // Identificador estable único POR INSTANCIA del DataTable.
              // Si dos DataTables en la misma página tienen filas con el
              // mismo _id (caso típico: la misma máquina aparece en la
              // tabla de ciclos normales y en la de abortados), antes
              // Framer Motion intentaba "mover" la fila de una tabla a
              // otra, dejando blancos/corrupciones visuales.
              const rowLocalId = `${layoutIdPrefix}${row._id || rowIndex}`;
              return (
              <motion.tr
                key={rowLocalId}
                custom={rowIndex}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={tableRowVariants}
                layoutId={rowLocalId}
                className="transition-colors duration-200 hover:bg-muted/30"
              >
                {/* Columna de acciones personalizada */}
                {!hideActionsColumn && columns.some(col => col.key === 'actions' || col.id === 'actions' || col.key === 'opciones' || col.id === 'opciones') && (() => {
                  const actionCol = columns.find(col => col.key === 'actions' || col.id === 'actions' || col.key === 'opciones' || col.id === 'opciones');
                  // Si la columna de acciones tiene un render personalizado, usarlo
                  if (actionCol?.render) {
                    return (
                      <td className="py-2 px-2 text-center w-[120px]">
                        <div className="flex justify-center items-center gap-1 flex-wrap sm:flex-nowrap">
                          {actionCol.render(row, actionCol, cellRenderProps)}
                        </div>
                      </td>
                    );
                  }
                  // Caso por defecto: renderizar acciones basadas en el array actions
                  return (
                    <td className="py-2 px-2 text-center w-[120px]">
                      <div className="flex justify-center items-center gap-1 flex-wrap sm:flex-nowrap">
                        {actionCol?.actions?.map(action => {
                          // Verificar si la acción debe mostrarse para esta fila
                          if (action.showWhen && !action.showWhen(row, cellRenderProps)) return null;
                          if (action.condition && !action.condition(row)) return null;
                          
                          // Determinar qué icono usar
                          let Icon;
                          switch(action.icon) {
                            case 'Eye': Icon = Eye; break;
                            case 'Edit': Icon = Edit; break;
                            case 'Trash': Icon = Trash; break;
                            case 'Check': Icon = Check; break;
                            case 'LogOut': Icon = LogOut; break;
                            case 'Shirt': Icon = Shirt; break;
                            case 'Printer': Icon = Printer; break;
                            case 'Bluetooth': Icon = Bluetooth; break;
                            case 'Trash2': Icon = Trash2; break;
                            case 'Settings': Icon = Settings; break;
                            case 'TrendingUp': Icon = TrendingUp; break;
                            case 'Truck': Icon = Truck; break;
                            case 'User': Icon = User; break;
                            case 'UserCheck': Icon = UserCheck; break;
                            case 'Users': Icon = Users; break;
                            case 'DollarSign': Icon = DollarSign; break;
                            case 'FileText': Icon = FileText; break;
                            case 'KeyRound': Icon = KeyRound; break;
                            case 'ShieldCheck': Icon = ShieldCheck; break;
                            case 'Building2': Icon = Building2; break;
                            default: Icon = Edit;
                          }
                          
                          return (
                            <Button
                              key={action.key}
                              onClick={() => onAction && onAction(action.key, row)}
                              variant="ghost"
                              size="icon"
                              className={`h-8 w-8 transition-colors hover:bg-black hover:text-white ${action.className || ''}`}
                              title={action.tooltip || ''}
                            >
                              <Icon className="h-4 w-4" />
                            </Button>
                          );
                        })}
                      </div>
                    </td>
                  );
                })()}
                
                {/* Columna de acciones genérica */}
                {!hideActionsColumn && !columns.some(col => col.key === 'actions' || col.id === 'actions' || col.key === 'opciones' || col.id === 'opciones') && (
                  <td className="py-2 px-2 text-center w-[120px]">
                    <div className="flex justify-center items-center gap-1 flex-wrap sm:flex-nowrap">
                      <Button
                        onClick={() => onAction && onAction('edit', row)}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 transition-colors hover:bg-black hover:text-white"
                      >
                        <Edit className="h-4 w-4 text-green-400" />
                      </Button>
                      <Button
                        onClick={() => onAction && onAction('delete', row)}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 transition-colors hover:bg-black hover:text-white"
                      >
                        <Trash className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  </td>
                )}
                {columns.filter(column => 
                  column.key !== 'opciones' && 
                  column.id !== 'opciones' && 
                  column.key !== 'actions' && 
                  column.id !== 'actions'
                ).map((column) => (
                  <td key={`${row._id}-${column.id}`} className="px-4 py-2 whitespace-nowrap" style={{width: column.width || 'auto'}}>
                    {column.render ? column.render(row, column, cellRenderProps) : renderCellContent(row[column.id], column.type)}
                  </td>
                ))}
              </motion.tr>
              );
            })}
          </AnimatePresence>
        </tbody>
      </table>
      {totalRecords > limit && (
        <div className="p-4 bg-card border-t border-border">
          <Pagination
            currentPage={page}
            totalItems={totalRecords}
            itemsPerPage={limit}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      )}
    </div>
  );
};

const renderCellContent = (value, type) => {
  if (value === undefined || value === null) {
    return <span className="text-muted-foreground italic">N/A</span>;
  }

  // Si el valor es un objeto complejo
  if (typeof value === 'object' && value !== null) {
    // Si tiene propiedad nombre, mostrar esa
    if (value.nombre) {
      return <span className="text-sm">{value.nombre}</span>;
    }
    // Si es un objeto lavandería o máquina con id pero sin nombre
    if (value.id) {
      return <span className="text-sm">ID: {value.id}</span>;
    }
    // Para fechas que pueden estar en formato de objeto
    if (value instanceof Date) {
      return (
        <span className="text-sm">
          {value.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </span>
      );
    }
    // Para cualquier otro objeto, mostrar [Objeto] para evitar el error
    return <span className="text-muted-foreground italic">[Objeto]</span>;
  }

  try {
    switch (type) {
      case 'number':
        // Verificar que sea realmente un número antes de llamar a toLocaleString
        if (typeof value === 'number') {
          return <span className="font-medium">{value.toLocaleString()}</span>;
        } else {
          const num = Number(value);
          return <span className="font-medium">{isNaN(num) ? '0' : num.toLocaleString()}</span>;
        }
      case 'boolean':
        return value ? (
          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold border border-green-200">
            Sí
          </span>
        ) : (
          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold border border-red-200">
            No
          </span>
        );
      case 'date':
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return <span className="text-muted-foreground italic">Fecha inválida</span>;
          }
          return (
            <span className="text-sm">
              {date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </span>
          );
        } catch (e) {
          return <span className="text-muted-foreground italic">Fecha inválida</span>;
        }
      case 'status':
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusClasses(value)}`}>
            {value}
          </span>
        );
      default:
        // Asegurarse de que sea una cadena antes de mostrarla
        return <span className="text-sm">{String(value)}</span>;
    }
  } catch (error) {
    console.error('Error rendering cell content:', error);
    return <span className="text-muted-foreground italic">Error de formato</span>;
  }
};

const getStatusClasses = (status) => {
  const s = status.toLowerCase();
  if (s === 'activo') return 'bg-green-700 text-white border-green-200';
  if (s === 'inactivo') return 'bg-red-700 text-white border-red-200';
  if (s === 'pendiente') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  if (s === 'completado') return 'bg-blue-100 text-blue-700 border-blue-200';
  if (s === 'cancelado') return 'bg-gray-100 text-gray-700 border-gray-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
};

export default DataTable;
